import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import Modal from '@/components/modal/Modal';
import SvgIcon from '@/components/SvgIcon';
import useSingleQuery from '@/hook/useSingleQuery';
import { isZeroOrDefined } from '@/lib/utils';
import type { ReRegistrationSuccessMemberStatus } from '@/api/feedDashboard';
import {
    fetchGetReRegistrationSuccessMembers,
} from '@/services/useFeedDashboardQueryService';
import type {
    ReRegistrationSuccessDataQuality,
    ReRegistrationSuccessMember,
    ReRegistrationSuccessMembersResponse,
} from '@/services/useFeedDashboardQueryService';
import { RE_REGISTRATION_SUCCESS_NAMES } from './coachPerformanceModel';

type Filter = 'ALL' | ReRegistrationSuccessMemberStatus;

/** 모달이 fetcher 에 넘기는 조회 조건. coachId 는 주입한 쪽이 넘겼을 때만 실린다. */
export type ReRegistrationSuccessMembersQueryParams = {
    centerId: string;
    cohortMonth: string;
    status?: ReRegistrationSuccessMemberStatus;
    search?: string;
    page: number;
    limit: number;
    coachId?: string;
};

/** `useSingleQuery` 의 fetchFn 모양. 기본값은 관리자웹 fetcher. */
export type ReRegistrationSuccessMembersFetcher = (params: ReRegistrationSuccessMembersQueryParams) => {
    queryKey: any[];
    queryFn: () => Promise<any>;
    onSuccess?: (data: any) => any;
};

type Props = {
    centerId: string;
    cohortMonth: string;
    successRate?: number | null;
    succeededMemberCount?: number;
    endedMemberCount?: number;
    notYetMemberCount?: number;
    asOf?: string;
    dataQuality?: ReRegistrationSuccessDataQuality;
    onClose: () => void;
    /*
     * ── 아래는 슈퍼관리자 대시보드가 쓰는 선택 props. 관리자웹은 넘기지 않으므로 기존 동작 그대로다. ──
     */
    /** 목록 fetcher 주입. 없으면 관리자웹 `/v1/centers/:centerId/...` */
    fetchMembers?: ReRegistrationSuccessMembersFetcher;
    /** 강사 근거 — fetcher params 에 실린다(값이 있을 때만) */
    coachId?: string;
    /** 회원명 검색창 표시 여부(기본 표시). 검색을 받지 않는 API 면 false */
    showSearch?: boolean;
    /** 제목 아래·요약 위에 끼우는 영역(지점 탭 등) */
    headerSlot?: React.ReactNode;
    /** 다시 시도해도 같은 오류(권한 없음 등)인지 — true 면 「다시 시도」 없이 안내만 */
    isUnavailableError?: (error: unknown) => boolean;
};

const qualityText: Record<ReRegistrationSuccessDataQuality, string> = {
    EXACT: '실시간 집계',
    ESTIMATED: '과거 데이터 추정치',
    MIXED: '실시간·과거 데이터 혼합',
};

const statusMeta: Record<ReRegistrationSuccessMemberStatus, { label: string; className: string }> = {
    SUCCEEDED: {
        label: '재등록 완료',
        className: 'border-[#BDE9D9] bg-[#EAF8F3] text-[#137A5D]',
    },
    NOT_YET_RE_REGISTERED: {
        label: '아직 미재등록',
        className: 'border-[#E4E7EC] bg-[#F5F6F8] text-[#69717D]',
    },
};

const formatDate = (value?: string | null) => (value ? dayjs(value).format('YYYY.MM.DD') : '-');

const ReRegistrationSuccessEvidenceModal = ({
    centerId,
    cohortMonth,
    successRate,
    succeededMemberCount,
    endedMemberCount,
    notYetMemberCount,
    asOf,
    dataQuality,
    onClose,
    fetchMembers = fetchGetReRegistrationSuccessMembers,
    coachId,
    showSearch = true,
    headerSlot,
    isUnavailableError,
}: Props) => {
    const [filter, setFilter] = useState<Filter>('ALL');
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [page, setPage] = useState(1);
    const limit = 8;

    // 지점 탭 등으로 조회 대상(centerId·coachId)이 바뀌면 1쪽부터 다시 본다.
    // (관리자웹은 대상이 바뀌지 않으므로 이 분기에 들어오지 않는다)
    const scopeKey = `${centerId}|${coachId ?? ''}`;
    const [prevScopeKey, setPrevScopeKey] = useState(scopeKey);
    if (prevScopeKey !== scopeKey) {
        setPrevScopeKey(scopeKey);
        setPage(1);
    }

    useEffect(() => {
        const timeoutId = window.setTimeout(() => setDebouncedSearch(search.trim()), 250);
        return () => window.clearTimeout(timeoutId);
    }, [search]);

    const { refinedResult, isLoading, isFetching, refresh } = useSingleQuery({
        fetchFn: fetchMembers,
        params: {
            centerId,
            cohortMonth,
            status: filter === 'ALL' ? undefined : filter,
            search: debouncedSearch || undefined,
            page,
            limit,
            ...(coachId ? { coachId } : {}),
        },
        enabled: !!centerId && !!cohortMonth,
        suspense: false,
    });

    const response = refinedResult?.data as ReRegistrationSuccessMembersResponse | undefined;
    const items = response?.items ?? [];
    const totalPages = response?.totalPages ?? 0;
    const isError = Boolean((refinedResult as any)?.isError);
    const isUnavailable = isError && Boolean(isUnavailableError?.((refinedResult as any)?.error));

    const tabs = useMemo(
        () => [
            { value: 'ALL' as Filter, label: '전체', count: endedMemberCount },
            {
                value: 'SUCCEEDED' as Filter,
                label: RE_REGISTRATION_SUCCESS_NAMES.numerator,
                count: succeededMemberCount,
            },
            {
                value: 'NOT_YET_RE_REGISTERED' as Filter,
                label: '아직 미재등록',
                count: notYetMemberCount,
            },
        ],
        [endedMemberCount, notYetMemberCount, succeededMemberCount],
    );

    const formulaRate = isZeroOrDefined(successRate) ? `${successRate}%` : '-';

    return (
        <Modal
            whiteViewWidth={1080}
            // headerSlot(지점 탭 등)이 들어오면 그만큼 키운다 — 780 은 기존 내용만으로 거의 꽉 차 목록이 잘린다
            whiteViewHeight={headerSlot ? 860 : 780}
            borderRadius={20}
            isCloseButton
            onClose={onClose}
            isCloseOutside
        >
            <div className="flex h-full flex-col px-[36px] pb-[28px] pt-[32px]">
                <div className="pr-[60px]">
                    <h2 className="text-[24px] font-semibold leading-[32px] tracking-[-0.5px] text-[#252A31]">
                        {RE_REGISTRATION_SUCCESS_NAMES.label} 산정 근거
                    </h2>
                    <p className="mt-[5px] text-[14px] leading-[20px] tracking-[-0.3px] text-[#7A828D]">
                        {dayjs(cohortMonth).format('YYYY년 MM월')}에 정상 종료한 회원이 이후 재등록했는지 보여드려요.
                    </p>
                </div>

                {headerSlot}

                <div className="mt-[22px] flex min-h-[116px] items-stretch rounded-[14px] border border-[#DCEFE9] bg-[#F6FBF9] px-[28px] py-[20px]">
                    <div className="flex w-[160px] flex-col justify-center border-r border-[#DCE8E4]">
                        <span className="text-[13px] font-medium text-[#60706B]">
                            {RE_REGISTRATION_SUCCESS_NAMES.label}
                        </span>
                        <span className="mt-[3px] text-[34px] font-bold leading-[42px] text-[#169873]">{formulaRate}</span>
                    </div>
                    <div className="flex flex-1 items-center justify-center gap-[18px] px-[26px]">
                        <CountBlock
                            label={RE_REGISTRATION_SUCCESS_NAMES.numerator}
                            value={succeededMemberCount}
                            emphasized
                        />
                        <span className="text-[24px] font-light text-[#9EA9A5]">÷</span>
                        <CountBlock label={RE_REGISTRATION_SUCCESS_NAMES.denominator} value={endedMemberCount} />
                        <span className="text-[18px] font-medium text-[#9EA9A5]">× 100</span>
                        <span className="text-[24px] font-light text-[#9EA9A5]">=</span>
                        <span className="min-w-[62px] text-[24px] font-bold text-[#26312E]">{formulaRate}</span>
                    </div>
                    <div className="flex w-[205px] flex-col justify-center border-l border-[#DCE8E4] pl-[24px]">
                        <span className="text-[12px] leading-[18px] text-[#7C8783]">
                            아직 미재등록은 실패가 아니며,
                            <br />이후 재등록 시 수치가 바뀔 수 있어요.
                        </span>
                        {(asOf || dataQuality) && (
                            <span className="mt-[7px] text-[11px] leading-[16px] text-[#96A09C]">
                                {asOf ? `${dayjs(asOf).format('YYYY.MM.DD HH:mm')} 기준` : ''}
                                {asOf && dataQuality ? ' · ' : ''}
                                {dataQuality ? qualityText[dataQuality] : ''}
                            </span>
                        )}
                    </div>
                </div>

                <div className="mt-[22px] flex items-end justify-between border-b border-[#E5E8EC]">
                    <div className="flex h-[43px] gap-[24px]">
                        {tabs.map((tab) => {
                            const isActive = filter === tab.value;
                            return (
                                <button
                                    key={tab.value}
                                    type="button"
                                    onClick={() => {
                                        setFilter(tab.value);
                                        setPage(1);
                                    }}
                                    className={`relative flex items-start gap-[5px] px-[2px] text-[14px] font-medium transition-colors ${
                                        isActive ? 'text-[#169873]' : 'text-[#767F8A] hover:text-[#3E464F]'
                                    }`}
                                >
                                    {tab.label}
                                    <span
                                        className={`rounded-full px-[7px] py-[1px] text-[11px] leading-[17px] ${
                                            isActive ? 'bg-[#E5F6F0] text-[#169873]' : 'bg-[#F2F4F6] text-[#8A929C]'
                                        }`}
                                    >
                                        {tab.count ?? '-'}
                                    </span>
                                    {isActive && <span className="absolute bottom-0 left-0 h-[2px] w-full bg-[#169873]" />}
                                </button>
                            );
                        })}
                    </div>
                    {showSearch && (
                        <label className="mb-[9px] flex h-[34px] w-[220px] items-center rounded-[8px] border border-[#DDE1E6] bg-white px-[10px] focus-within:border-[#8DCDBA]">
                            <SvgIcon name="SvgSearch" size={15} stroke="#98A0AA" fill="transparent" />
                            <input
                                value={search}
                                onChange={(event) => {
                                    setSearch(event.target.value);
                                    setPage(1);
                                }}
                                placeholder="회원명 검색"
                                className="ml-[7px] min-w-0 flex-1 border-0 bg-transparent text-[13px] text-[#333A43] outline-none placeholder:text-[#ABB1B9]"
                            />
                        </label>
                    )}
                </div>

                <div className="mt-[12px] min-h-0 flex-1 overflow-hidden rounded-[10px] border border-[#E4E7EB]">
                    <div className="grid h-[42px] grid-cols-[1.05fr_1.55fr_0.85fr_1fr_1.55fr] items-center bg-[#F7F8FA] px-[20px] text-[12px] font-medium text-[#707985]">
                        <span>회원</span>
                        <span>종료 수강권</span>
                        <span>기존 담당</span>
                        <span>재등록 상태</span>
                        <span>재등록 정보</span>
                    </div>
                    <div className="relative h-[320px] overflow-y-auto bg-white">
                        {isLoading ? (
                            <CenteredState text="산정 근거를 불러오고 있어요." />
                        ) : isUnavailable ? (
                            <div className="flex h-full flex-col items-center justify-center">
                                <span className="text-[14px] text-[#606975]">산정 근거를 불러올 수 없어요.</span>
                                <span className="mt-[6px] text-[12px] text-[#98A0AA]">
                                    이 지점의 근거를 볼 권한이 없거나 지점 정보를 찾지 못했어요.
                                </span>
                            </div>
                        ) : isError ? (
                            <div className="flex h-full flex-col items-center justify-center">
                                <span className="text-[14px] text-[#606975]">목록을 불러오지 못했어요.</span>
                                <button
                                    type="button"
                                    onClick={() => refresh()}
                                    className="mt-[10px] rounded-[7px] border border-[#D8DDE3] px-[14px] py-[6px] text-[12px] text-[#505963] hover:bg-[#F7F8FA]"
                                >
                                    다시 시도
                                </button>
                            </div>
                        ) : items.length === 0 ? (
                            <CenteredState
                                text={debouncedSearch ? '검색 결과가 없어요.' : '해당 조건의 회원이 없어요.'}
                            />
                        ) : (
                            items.map((item) => <EvidenceRow key={item.memberId} item={item} />)
                        )}
                        {isFetching && !isLoading && (
                            <div className="absolute inset-0 flex items-center justify-center bg-white/65 text-[13px] text-[#6E7782]">
                                목록을 업데이트하고 있어요.
                            </div>
                        )}
                    </div>
                </div>

                <div className="mt-[14px] flex h-[30px] items-center justify-between">
                    <span className="text-[12px] text-[#8B939D]">
                        {showSearch ? '검색 결과' : '회원'} {response?.total ?? 0}명 · 같은 달 여러 수강권이 종료된 회원은 1명으로 계산해요.
                    </span>
                    <div className="flex items-center gap-[10px]">
                        <button
                            type="button"
                            aria-label="이전 페이지"
                            disabled={page <= 1 || isFetching}
                            onClick={() => setPage((current) => Math.max(1, current - 1))}
                            className="flex h-[28px] w-[28px] items-center justify-center rounded-[6px] border border-[#DDE1E6] disabled:cursor-default disabled:opacity-35"
                        >
                            <SvgIcon name="SvgLeftStrokeArrow" size={12} stroke="#59616B" fill="transparent" />
                        </button>
                        <span className="min-w-[48px] text-center text-[12px] text-[#5D6570]">
                            {totalPages > 0 ? page : 0} / {totalPages}
                        </span>
                        <button
                            type="button"
                            aria-label="다음 페이지"
                            disabled={page >= totalPages || isFetching}
                            onClick={() => setPage((current) => current + 1)}
                            className="flex h-[28px] w-[28px] items-center justify-center rounded-[6px] border border-[#DDE1E6] disabled:cursor-default disabled:opacity-35"
                        >
                            <SvgIcon name="SvgRightStrokeArrow" size={12} stroke="#59616B" fill="transparent" />
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

const CountBlock = ({ label, value, emphasized = false }: { label: string; value?: number; emphasized?: boolean }) => (
    <div className="flex min-w-[76px] flex-col items-center">
        <span className="text-[12px] text-[#73807B]">{label}</span>
        <span className={`mt-[3px] text-[22px] font-bold ${emphasized ? 'text-[#169873]' : 'text-[#2E3835]'}`}>
            {value === undefined ? '-' : `${value}명`}
        </span>
    </div>
);

const CenteredState = ({ text }: { text: string }) => (
    <div className="flex h-full items-center justify-center text-[14px] text-[#7B848F]">{text}</div>
);

const EvidenceRow = ({ item }: { item: ReRegistrationSuccessMember }) => {
    const meta = statusMeta[item.status];
    return (
        <div className="grid min-h-[64px] grid-cols-[1.05fr_1.55fr_0.85fr_1fr_1.55fr] items-center border-b border-[#EDF0F2] px-[20px] last:border-b-0">
            <span className="truncate pr-[14px] text-[13px] font-medium text-[#313840]">{item.memberName || '-'}</span>
            <div className="min-w-0 pr-[14px]">
                <p className="truncate text-[13px] text-[#3C444D]">{item.endedMembershipName || '-'}</p>
                <p className="mt-[3px] text-[11px] text-[#949BA4]">{formatDate(item.endedAt)} 정상 종료</p>
            </div>
            <span className="truncate pr-[12px] text-[13px] text-[#59626D]">{item.previousCoachName || '-'}</span>
            <div className="flex flex-col items-start gap-[3px]">
                <span className={`rounded-full border px-[9px] py-[3px] text-[11px] font-medium ${meta.className}`}>
                    {meta.label}
                </span>
                <span className="pl-[3px] text-[10px] text-[#A0A7AF]">{qualityText[item.dataQuality]}</span>
            </div>
            <div className="min-w-0">
                {item.status === 'SUCCEEDED' ? (
                    <>
                        <p className="truncate text-[13px] text-[#3C444D]">{item.targetMembershipName || '-'}</p>
                        <p className="mt-[3px] truncate text-[11px] text-[#949BA4]">
                            {formatDate(item.registeredAt)} · {item.newCoachName || '담당 강사 없음'}
                        </p>
                    </>
                ) : (
                    <p className="text-[12px] text-[#9AA1AA]">조회 시점까지 재등록 없음</p>
                )}
            </div>
        </div>
    );
};

export default ReRegistrationSuccessEvidenceModal;
